import Logo from 'assets/icons/logo_navbar_w.svg';
import LogoMobile from 'assets/icons/logo_two_tone.svg'
import Button from 'components/Button/Button';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { useSession } from 'contexts';
import { Link, useLocation } from 'react-router-dom';
import { OrgTypes } from 'utils/orgTypes';
import { useEffect, useState } from 'react';
import { FullFormikCheckBoxSmall } from 'components/Form/StandardForm';
import { useLazyQuery } from '@apollo/client';
import { GetOrgSubtypeDocument } from 'graphql/generated';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';

const loginValues = {
  email: '',
  password: '',
  showPassword: ''
};

const SignIn = (props) => {

  const { login, setOrganizationId, setOrganizationType, organizationType, user, organizationId, logout } = useSession();
  const [showPassword, setShowPassword] = useState(false);
  const { loginType, token } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [ getOrgSubType, { client } ] = useLazyQuery(GetOrgSubtypeDocument);
  let isMroLogin = location.pathname.includes('/mro/auth');
  const handleLogin = async (email, password, impersonate = false) => {
    try {
      const userLogin = await login(email, password, impersonate);
      
      if(userLogin.mroEmployeeProfileId && !isMroLogin){
        const confirmNotMro = window.confirm("You are not logging in as an MRO Employee. Would you like to continue?");  
        if(!confirmNotMro){
          logout();
          localStorage.clear();
          await client.clearStore();
          await client.cache.reset();
          navigate('/mro/auth');
          return;
        }
      }

      if(isMroLogin){ // MRO LOGIN
        if(userLogin.mroEmployeeProfileId){
          setOrganizationType( OrgTypes.PART145 );
          navigate('/mro/dashboard');
        }else{
          alert("Error: Employee is not tied to an organization");
        }
      } else { // STANDARD LOGIN
        if(userLogin?.organizationId){
          setOrganizationType( OrgTypes.PART91 );
          setOrganizationId(userLogin.organizationId);
          navigate('/aircraft');
        }else{
          navigate('/sign-up');
        }
      }
    } catch (error: any) {
      alert(error.message);
    }
  };
  // process login
  useEffect(() => {
    const processLoginTypes = async () => {
      if (loginType === 'impersonate') {
        localStorage.clear();
        await client.clearStore();
        await client.cache.reset();
        handleLogin(decodeURIComponent(token), "", true); 
      }
      if (loginType === "error") {
        alert("Error: Sign In failed - please try again");
        const newUrl = window.location.href.replace('/auth/error', '/auth');
        window.history.replaceState({}, document.title, newUrl);
      }
      if (loginType === "error-sign-failed") {
        if (window.confirm("Error: Unable to sign in - redirecting to legacy sign in page")) {
          window.location.href = 'https://app.coflyt.com/login';
        }
      }
      if (loginType === "user-migrated") {
        alert("You have been migrated to the our new system. Please login again with your email and password");
        const newUrl = window.location.href.replace('/auth/user-migrated', '/auth');
        window.history.replaceState({}, document.title, newUrl);
      }
    };

    processLoginTypes();
  }, [loginType, token]);

  useEffect(() => {
    if (user && organizationId && (organizationType === OrgTypes.PART91 || !organizationType)) {
      setOrganizationType( OrgTypes.PART91 );
      setOrganizationId(organizationId);
      navigate('/crafts');
    }else if (user && organizationType === OrgTypes.PART145) {
      setOrganizationType( OrgTypes.PART145 );
      navigate('/mro/dashboard');
    }
  }, [user]);
   
  return (
    <>
      <div className="flex flex-col justify-center items-center w-full md:w-[26rem] bg-white md:bg-transparent">
        <div className="flex justify-center w-screen min-w-[90vw] z-[1] h-full pt-5 md:pt-10 md:h-auto md:border-y border-brand-pale border-dashed md:py-4">
          <div className="w-full rounded border-slate-300 md:border-b-4 md:w-96 md:pt-5 bg-white md:shadow-blue">
            <Formik
              initialValues={loginValues}
              onSubmit={async (values: typeof loginValues) => { handleLogin(values.email, values.password) }}
            >
              {({ values }) => (
                <Form className="">
                  <div id="ribbon" className="rounded-tl text-white text-center text-2xl font-bold pl-10 pr-8 -ml-2 bg-brand relative border-b-2 border-r-1 border-t border-t-brand border-[#162551] rounded-r w-full">
                    <img alt="logo" className="w-32 py-2" src={Logo} />
                    {/* <img alt="logo" className="w-32 py-2 block md:hidden" src={LogoMobile} /> */}
                    {/* <hr className="border-brand-pale border-dashed mt-4 -mb-2 md:hidden"/> */}
                  </div>
                  <div className="flex flex-col p-10">
                    <div className="flex flex-col mb-6">
                      <span className="text-2xl font-bold text-brand mb-1">
                        {!isMroLogin 
                          ? "Log in to your account"
                          : "MRO Employee Login"
                        }
                      </span>
                      {/* <span className="md:text-sm text-slate-500">Fly with pilot peace of mind.</span> */}
                    </div>
                    <div className="flex flex-col relative">
                      <label className="text-sm font-medium text-slate-500 mb-1">Email</label>
                      <Field
                        name="email"
                        type="email"
                        className="rounded border-slate-300 bg-slate-50 h-12 md:h-10 mb-3 px-2 text-brand focus:shadow-inner focus:bg-white placeholder:text-slate-400 placeholder:text-sm"></Field>
                      <ErrorMessage name="email" component="div" className="text-red-500 text-xs italic" />
                    </div>
                    <div className="flex flex-col relative">
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-sm font-medium text-slate-500">Password</label>
                        <Link to={isMroLogin ? '/auth/forgot-password/mro' : '/auth/forgot-password'} className="text-brand-electric font-semibold text-xs">
                          Forgot password?
                        </Link>
                      </div>
                      <Field
                        name="password"
                        type={ !showPassword && "password"}
                        className="rounded border-slate-300 bg-slate-50 h-12 md:h-10 mb-3 pl-2 pr-10 text-brand focus:shadow-inner focus:bg-white placeholder:text-slate-400 placeholder:text-sm"></Field>
                      <ErrorMessage name="password" component="div" className="text-red-500 text-xs italic" />
                      <div className="hidden">
                        <FullFormikCheckBoxSmall name="showPassword" isVertical={true} label='Show Password' />
                      </div>
                      <div 
                        onClick={() => {setShowPassword(!showPassword)}} 
                        className={`${showPassword ? 'text-slate-500' : 'text-slate-400'} absolute right-2 bottom-6 md:bottom-5 hover:text-slate-300 cursor-pointer transition`}
                      >
                        {showPassword 
                          ? <EyeIcon className="h-6 w-6 p-0.5"/>
                          : <EyeSlashIcon className="h-6 w-6 p-0.5"/>
                        }
                      </div>
                    </div>
                    <Button text="Log In" color="navy" size="sm" type="submit" className="mt-2"/>
                    <div className="flex flex-col justify-start border-b border-brand-pale border-dashed mt-10 mb-8 w-full relative">
                      <div className="flex items-center justify-center absolute -mt-3 w-16 bg-white left-[calc(50%-2rem)] text-slate-500">
                        <span>Or</span>
                      </div>
                    </div>
                    <div className="flex w-full mt-2 gap-1">
                      <Link to={'https://app.coflyt.com/login'} className="grow">
                        <Button text="Legacy Login" color="offwhite" size="xs" className="w-full"/>
                      </Link>
                      { isMroLogin 
                        ? <Link to={'/auth'} className="w-1/2"><Button text="Pilot Login" color="offwhite" size="xs" className="w-full"/></Link> 
                        : <Link to={'/mro/auth'} className="w-1/2"><Button text="MRO Login" color="offwhite" size="xs" className="w-full"/></Link>
                      }
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
        {!isMroLogin && 
          <span className="mt-2 pb-10 md:pb-0 z-[1]">Don't have an account? <a href="/sign-up" className="text-brand-electric font-semibold">Sign up</a></span>
        }
      </div>
      <div className="absolute z-[0] w-[26rem] h-screen hidden md:block border-x border-brand-pale border-dashed"></div>
    </>
  );
};


export default SignIn;
